import React, { useEffect, useState } from "react";

/**
 * PageAdmin — generic editor for the block-based Page CMS.
 * Props: authed (axios instance), slug (mission | about | get_involved | volunteer | stories | raffle), title.
 *
 * Backend contract:
 *   GET  /admin/page/{slug}/preview       — sections (ignores state)
 *   GET  /admin/page/{slug}/meta          — {state, seo}
 *   PUT  /admin/page/{slug}/meta          — {state?, seo?}
 *   POST /admin/page/{slug}/publish-batch — publish drafts
 *   POST /admin/page/{slug}/seed-missing  — idempotent seed
 *   PATCH /content/{id}/draft             — save draft
 *   POST  /content/{id}/publish           — publish section
 */
export default function PageAdmin({ authed, slug, title }) {
  const [meta, setMeta] = useState({ state: "draft", seo: {} });
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [msg, setMsg] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [m, p] = await Promise.all([
        authed.get(`/admin/page/${slug}/meta`),
        authed.get(`/admin/page/${slug}/preview`),
      ]);
      setMeta(m.data);
      setSections(p.data.sections || []);
    } catch (e) {
      setMsg(e?.response?.data?.detail || String(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  const setState = async (state) => {
    await authed.put(`/admin/page/${slug}/meta`, { state });
    setMeta(m => ({ ...m, state }));
    setMsg(`State → ${state}`);
  };

  const saveSeo = async (seo) => {
    await authed.put(`/admin/page/${slug}/meta`, { seo });
    setMeta(m => ({ ...m, seo }));
    setMsg("SEO saved.");
  };

  const seedMissing = async () => {
    const r = await authed.post(`/admin/page/${slug}/seed-missing`, {});
    setMsg(`Inserted ${r.data.inserted} sections.`);
    load();
  };

  const publishAll = async () => {
    const r = await authed.post(`/admin/page/${slug}/publish-batch`, { dry_run: false });
    setMsg(`Published ${r.data.published_count} sections.`);
    load();
  };

  const saveSection = async (sec, patch) => {
    const nextData = { ...(sec.data || sec), ...patch };
    // strip API scaffolding fields
    delete nextData.id;
    await authed.put(`/content/${sec.id}/draft`, { data: nextData });
    await authed.post(`/content/${sec.id}/publish`, {});
    setMsg(`Saved "${sec.data?.slug || sec.slug}".`);
    load();
  };

  return (
    <div data-testid={`page-admin-${slug}`} className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-editorial text-2xl">{title}</h2>
          <p className="text-ink/60 text-sm">Edit blocks live — publish saves immediately.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span data-testid={`page-state-${slug}`}
                className={`label-mono text-[10px] px-2 py-1 rounded ${meta.state === "live" ? "bg-terracotta/10 text-terracotta" : "bg-ink/10 text-ink/70"}`}>
            {(meta.state || "draft").toUpperCase()}
          </span>
          <button onClick={() => setState("draft")}
                  data-testid={`page-state-draft-${slug}`}
                  className="text-xs border border-line px-3 py-1.5">Draft</button>
          <button onClick={() => setState("ready")}
                  data-testid={`page-state-ready-${slug}`}
                  className="text-xs border border-line px-3 py-1.5">Ready</button>
          <button onClick={() => setState("live")}
                  data-testid={`page-state-live-${slug}`}
                  className="text-xs bg-terracotta text-white px-3 py-1.5">Set live</button>
          <button onClick={seedMissing}
                  data-testid={`page-seed-${slug}`}
                  className="text-xs border border-line px-3 py-1.5">Seed missing</button>
          <button onClick={publishAll}
                  data-testid={`page-publish-all-${slug}`}
                  className="text-xs border border-line px-3 py-1.5">Publish all</button>
        </div>
      </header>
      {msg && <div className="text-sm text-terracotta">{msg}</div>}

      <SeoEditor seo={meta.seo || {}} onSave={saveSeo} slug={slug} />

      {loading ? <div className="text-ink/50">Loading…</div> : (
        <div className="border border-line rounded" data-testid={`page-sections-${slug}`}>
          <table className="w-full text-sm">
            <thead className="bg-line/40">
              <tr>
                {["order", "slug", "template", "visible", ""].map(c =>
                  <th key={c} className="text-left p-3 label-mono">{c}</th>)}
              </tr>
            </thead>
            <tbody>
              {sections.map(sec => {
                const d = sec.data || sec;
                return (
                  <tr key={sec.id} className="border-t border-line">
                    <td className="p-3">{d.order}</td>
                    <td className="p-3 font-mono text-xs">{d.slug}</td>
                    <td className="p-3">{d.template}</td>
                    <td className="p-3">{d.visible === false ? "—" : "✓"}</td>
                    <td className="p-3 text-right space-x-3">
                      <button data-testid={`section-edit-${slug}-${d.slug}`}
                              onClick={() => setEditing(sec)}
                              className="label-mono text-terracotta">Edit</button>
                      <button data-testid={`section-toggle-visible-${slug}-${d.slug}`}
                              onClick={() => saveSection(sec, { visible: !(d.visible !== false) })}
                              className="label-mono text-ink/60">
                        {d.visible === false ? "Show" : "Hide"}
                      </button>
                    </td>
                  </tr>
                );
              })}
              {sections.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-ink/50">No sections yet — click <em>Seed missing</em>.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {editing && <SectionEditor slug={slug} section={editing}
                                 onClose={() => setEditing(null)}
                                 onSave={async (patch) => { await saveSection(editing, patch); setEditing(null); }} />}
    </div>
  );
}

function SeoEditor({ seo, onSave, slug }) {
  const [t, setT] = useState({ en: "", da: "", es: "" });
  const [d, setD] = useState({ en: "", da: "", es: "" });
  useEffect(() => {
    setT({ en: seo?.title?.en || "", da: seo?.title?.da || "", es: seo?.title?.es || "" });
    setD({ en: seo?.description?.en || "", da: seo?.description?.da || "", es: seo?.description?.es || "" });
  }, [seo]);
  const save = () => onSave({ title: t, description: d });
  return (
    <details className="border border-line rounded p-4" data-testid={`seo-editor-${slug}`}>
      <summary className="cursor-pointer label-mono text-terracotta">SEO — title & description (EN · DA · ES)</summary>
      <div className="mt-4 grid md:grid-cols-2 gap-4">
        <FieldGroup label="Title" value={t} onChange={setT} testidPrefix={`seo-title-${slug}`} />
        <FieldGroup label="Description" value={d} onChange={setD} multiline testidPrefix={`seo-desc-${slug}`} />
      </div>
      <div className="mt-3 flex gap-2">
        <button onClick={save} data-testid={`seo-save-${slug}`}
                className="text-xs bg-terracotta text-white px-3 py-1.5">Save SEO</button>
      </div>
    </details>
  );
}

function FieldGroup({ label, value, onChange, multiline, testidPrefix }) {
  return (
    <div>
      <div className="label-mono text-ink/60 text-[10px] mb-2">{label}</div>
      {["en", "da", "es"].map(l => (
        <div key={l} className="mb-2">
          <div className="label-mono text-ink/50 text-[9px]">{l.toUpperCase()}</div>
          {multiline ? (
            <textarea rows={3} value={value[l] || ""} data-testid={`${testidPrefix}-${l}`}
                      onChange={e => onChange({ ...value, [l]: e.target.value })}
                      className="w-full border border-line px-2 py-1.5 text-sm mt-1" />
          ) : (
            <input value={value[l] || ""} data-testid={`${testidPrefix}-${l}`}
                   onChange={e => onChange({ ...value, [l]: e.target.value })}
                   className="w-full border border-line px-2 py-1.5 text-sm mt-1" />
          )}
        </div>
      ))}
    </div>
  );
}

function SectionEditor({ slug, section, onClose, onSave }) {
  const initial = section.data || section;
  const [data, setData] = useState(initial);
  const setTx = (key, l, v) => setData(x => ({ ...x, [key]: { ...(x[key] || {}), [l]: v } }));
  const setPlain = (key, v) => setData(x => ({ ...x, [key]: v }));

  // Common fields the templates use — render only when the CURRENT value has
  // the shape we can edit. Preserves any admin-added keys.
  const has = (k) => Object.prototype.hasOwnProperty.call(data, k);
  const isTri = (v) => v && typeof v === "object" && ("en" in v || "da" in v || "es" in v);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
         onClick={onClose}>
      <div className="bg-white w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded p-6"
           onClick={e => e.stopPropagation()}
           data-testid={`section-editor-${slug}-${data.slug || ""}`}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="label-mono text-ink/50 text-[10px]">{slug} / {data.template}</div>
            <h3 className="font-editorial text-2xl">{data.slug}</h3>
          </div>
          <button onClick={onClose} className="label-mono text-ink/50">✕ Close</button>
        </div>

        <div className="space-y-5">
          {has("order") && (
            <Row label="Order">
              <input type="number" value={data.order || 0}
                     onChange={e => setPlain("order", parseInt(e.target.value || "0", 10))}
                     data-testid="section-field-order"
                     className="border border-line px-2 py-1.5 text-sm w-32" />
            </Row>
          )}
          {["kicker", "eyebrow", "headline", "body", "caption", "cta_label", "cta_url", "cta2_label", "cta2_url", "cta3_label", "cta3_url",
            "left_label", "left_body", "right_label", "right_body", "right_href",
            "volunteer_label", "company_label", "success_headline",
            "draw_source", "draw_iso_override",
            "form_recipient", "anchor",
          ].filter(k => has(k)).map(k => (
            <Row key={k} label={k}>
              {isTri(data[k]) ? (
                <FieldGroup label="" value={data[k]} onChange={v => setPlain(k, v)}
                            multiline={["body", "caption"].includes(k)}
                            testidPrefix={`section-tx-${k}`} />
              ) : (
                <input value={data[k] || ""} onChange={e => setPlain(k, e.target.value)}
                       data-testid={`section-field-${k}`}
                       className="w-full border border-line px-2 py-1.5 text-sm" />
              )}
            </Row>
          ))}

          {/* Images editor — supports multiple images (reorderable), per-image
              URL + EN/DA/ES alt text. Each content block can have its own
              independent image(s). */}
          <Row label="Images (ordered)">
            {(data.images || []).map((img, i) => (
              <div key={i} className="mb-4 border border-line rounded p-3">
                <div className="flex items-start gap-3">
                  <input value={img.url || ""}
                         onChange={e => {
                           const next = [...(data.images || [])];
                           next[i] = { ...img, url: e.target.value };
                           setPlain("images", next);
                         }}
                         data-testid={`section-image-url-${i}`}
                         placeholder="Image URL (leave blank to keep the current one empty)"
                         className="flex-1 border border-line px-2 py-1.5 text-sm" />
                  <button type="button"
                          onClick={() => {
                            const next = [...(data.images || [])];
                            next.splice(i, 1);
                            setPlain("images", next);
                          }}
                          data-testid={`section-image-remove-${i}`}
                          className="text-xs text-red-600 hover:underline">Remove</button>
                </div>
                {img.url && <img src={img.url} alt="" className="mt-2 max-h-28 border border-line rounded" />}
                <div className="mt-2">
                  <div className="label-mono text-ink/60 text-[10px] mb-1">Alt text (EN / DA / ES)</div>
                  <FieldGroup label="" value={img.alt || {}}
                              onChange={v => {
                                const next = [...(data.images || [])];
                                next[i] = { ...img, alt: v };
                                setPlain("images", next);
                              }}
                              testidPrefix={`section-image-alt-${i}`} />
                </div>
              </div>
            ))}
            <button type="button"
                    onClick={() => setPlain("images", [...(data.images || []), { url: "", alt: { en: "", da: "", es: "" } }])}
                    data-testid="section-image-add"
                    className="mt-1 text-xs border border-line px-3 py-1.5 hover:border-ink">+ Add image</button>
          </Row>
        </div>

        <div className="mt-6 flex gap-3">
          <button onClick={() => onSave(data)} data-testid={`section-save-${slug}`}
                  className="bg-terracotta text-white px-6 py-2 text-sm">Save & publish</button>
          <button onClick={onClose} className="border border-line px-6 py-2 text-sm">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div>
      <div className="label-mono text-ink/60 text-[10px] mb-1">{label}</div>
      {children}
    </div>
  );
}
